<template>
    <div ref="sortableList" class="draggable-list">
        <slot />
        <div v-if="displayDragArea" :style="styleDragArea" class="drag-area" />
    </div>
</template>

<script>
import Sortable from 'sortablejs';

export default {
    name: 'SaasDraggable',
    props: {
        value: {
            type: Array,
            required: true,
        },
        group: {
            type: String,
            default: 'default-group',
        },
        useDragArea: {
            type: Boolean,
            default: false,
        },
        dragClass: {
            type: String,
            default: 'draggable-item--draggable',
        },
        defaultDragZoneHeight: {
            type: String,
            default: '100px',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        filterClasses: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            draggedItem: null,
            heightItem: '0px',
            sortable: null,
            isDragging: false,
            upListener: null,
        };
    },
    computed: {
        styleDragArea() {
            return {
                height: this.heightItem,
            };
        },
        displayDragArea() {
            if (!this.useDragArea) {
                return false;
            }
            return !this.value.length || (!!this.draggedItem && this.value.length > 0);
        },
    },
    watch: {
        value: {
            handler(newValue, oldValue) {
                if (newValue.length !== oldValue.length) {
                    this.$nextTick(() => {
                        this.computeStyle();
                    });
                }
            },
            deep: true,
        },
        disabled(newValue) {
            if (this.sortable) {
                this.sortable.option('disabled', newValue);
            }
        },
    },
    mounted() {
        this.sortable = Sortable.create(this.$refs.sortableList, {
            forceFallback: true,
            fallbackOffset: { x: 0, y: 0 },
            fallbackOnBody: true,
            filter: this.filterClasses,
            group: {
                name: this.group,
                pull: true,
                put: true,
            },
            onChoose: () => {
                this.isDragging = true;
            },
            onUnchoose: this.cleanup,
            onStart: (event) => {
                this.draggedItem = this.value[event.oldIndex];
            },
            onMove: (event) => {
                const draggedItem = this.getDraggedItem(event);
                this.emitChanges({ moved: this.getDraggedItem(event) });
                if (draggedItem.editable !== undefined && !draggedItem.editable) {
                    return false;
                }
            },
            onUpdate: (event) => {
                if (event.oldIndex === event.newIndex) {
                    return;
                }
                const newIndex = event.newIndex;
                const fromList = event.from.__vue__;
                const draggedItem = fromList.draggedItem;
                const itemsCopy = [...this.value];

                itemsCopy.splice(event.oldIndex, 1);
                itemsCopy.splice(newIndex, 0, draggedItem);

                this.$emit('input', itemsCopy);
                this.emitChanges({ updated: this.getDraggedItem(event) });
            },
            onAdd: (event) => {
                this.removeNode(event.item);

                const draggedItem = this.getDraggedItem(event);
                const newIndex = event.newIndex;
                const itemsCopy = [...this.value];
                itemsCopy.splice(newIndex, 0, draggedItem);

                this.$emit('input', itemsCopy);
                this.emitChanges({ added: this.getDraggedItem(event) });
            },
            onRemove: (event) => {
                this.insertNodeAt(this.$el, event.item, event.oldIndex);
                const itemsCopy = [...this.value];
                itemsCopy.splice(event.oldIndex, 1);
                this.$emit('input', itemsCopy);

                this.emitChanges({ removed: this.getDraggedItem(event) });
            },
            onEnd: () => {
                this.draggedItem = null;
            },
        });
        this.computeStyle();
    },
    beforeDestroy() {
        if (this.sortable) {
            this.sortable.destroy();
            this.sortable = null;
        }
        this.cleanup();
    },
    methods: {
        cleanup() {
            this.isDragging = false;
            const item = document.querySelector('.sortable-drag');
            if (item) {
                item.classList.remove('sortable-drag');
            }
        },
        computeStyle() {
            const draggableItems = document.getElementsByClassName(this.dragClass);

            for (const draggableItem of draggableItems) {
                draggableItem.classList.add('saas-item--draggable');
                if (this.disabled) {
                    draggableItem.classList.add('sortable-disabled');
                } else {
                    draggableItem.classList.remove('sortable-disabled');
                }
            }

            const draggableItem = draggableItems.item(0);
            this.heightItem = draggableItem
                ? `${draggableItem.offsetHeight}px`
                : this.defaultDragZoneHeight;
        },
        emitChanges(event) {
            this.$nextTick(() => {
                this.$emit('change', event);
            });
        },
        getDraggedItem(event) {
            return event?.from?.__vue__?.draggedItem;
        },
        removeNode(node) {
            if (node.parentElement !== null) {
                node.parentElement.removeChild(node);
            }
        },
        insertNodeAt(parentNode, node, position) {
            const refNode =
                position === 0
                    ? parentNode.children[0]
                    : parentNode.children[position - 1].nextSibling;
            parentNode.insertBefore(node, refNode);
        },
    },
};
</script>

<style lang="scss">
.saas-item--draggable:not(.sortable-disabled) {
    cursor: pointer;

    &.sortable-chosen {
        cursor: grabbing;
    }

    &.sortable-chosen.sortable-ghost {
        background-color: var(--colors-common-background-primary-subtle);
        border: none;
        cursor: grabbing;

        > * {
            visibility: hidden;
        }
    }
}

.draggable-list {
    display: flex;
    flex-direction: column;

    &:has(.sortable-chosen) > .saas-item--draggable:not(.sortable-disabled) {
        cursor: grabbing;
    }
}

body:has(.sortable-fallback) {
    user-select: none;
    cursor: grabbing;
}

.sortable-fallback {
    box-shadow: var(--box-shadows-small);
    cursor: grabbing;
    height: fit-content !important;
    list-style-type: none;
}
</style>
